/**
 * Thin analytics layer — pushes to GTM dataLayer when available and
 * forwards to gtag for GA4. Safe to call before tags load; events are
 * queued in dataLayer and consumed once GTM/gtag boot.
 *
 * Use trackEvent() for ALL conversion tracking so we have a single source
 * of truth for event names and parameters across the app.
 */

import { GA4_MEASUREMENT_ID, GOOGLE_ADS_CONVERSION_SEND_TO } from '@/config/analytics';
import { canTrackAds } from '@/lib/consent';

declare global {
  interface Window {
    dataLayer?: any[];
    gtag?: (...args: any[]) => void;
  }
}

type EventName =
  | 'page_view'
  | 'phone_click'
  | 'whatsapp_click'
  | 'cta_click'
  | 'form_submit'
  | 'form_submit_error'
  // English landing pages (expat / international funnel)
  | 'english_page_view'
  | 'english_phone_click'
  | 'english_whatsapp_click'
  | 'english_booking_click'
  | 'english_directions_click';

export interface EventParams {
  /** Free-form label, e.g. service slug or section name. */
  label?: string;
  /** Page path the event fired on. Auto-filled if omitted. */
  page_path?: string;
  /** Page title. Auto-filled if omitted. */
  page_title?: string;
  /** Any extra custom params; forwarded as-is. */
  [key: string]: any;
}

const isBrowser = typeof window !== 'undefined';

function ensureDataLayer(): any[] {
  if (!isBrowser) return [];
  window.dataLayer = window.dataLayer || [];
  return window.dataLayer;
}

/**
 * Record an event in BOTH the GTM dataLayer (event name) AND GA4 via gtag.
 * GTM tags can subscribe via "Custom Event" triggers; GA4 receives it as a
 * named event with the same parameters.
 */
export function trackEvent(name: EventName, params: EventParams = {}) {
  if (!isBrowser) return;

  const enriched: EventParams = {
    page_path: window.location.pathname + window.location.search,
    page_title: document.title,
    ...params,
  };

  // GTM-style event (always queued; consumed when GTM loads).
  ensureDataLayer().push({ event: name, ...enriched });

  // GA4 via gtag (queued if gtag not yet loaded — gtag stub is set in index.html).
  if (typeof window.gtag === 'function') {
    window.gtag('event', name, enriched);
  }

  // Google Ads conversion "Zakazivanje / kontakt" — the customer actions that
  // measure a booking intent. Blocked in consent/unknown regions (no-banner route);
  // blocked events are never queued for replay.
  if (
    GOOGLE_ADS_CONVERSION_SEND_TO &&
    (name === 'phone_click' || name === 'whatsapp_click' || name === 'form_submit')
  ) {
    void canTrackAds().then((allowed) => {
      if (allowed && typeof window.gtag === 'function') {
        window.gtag('event', 'conversion', { send_to: GOOGLE_ADS_CONVERSION_SEND_TO });
      }
    });
  }
}

/**
 * Fire a GA4 page_view manually after client-side navigation.
 * Called by usePageTracking() on every route change.
 */
export function trackPageView(pathname: string, title?: string) {
  if (!isBrowser) return;
  const url = window.location.origin + pathname;

  ensureDataLayer().push({
    event: 'page_view',
    page_path: pathname,
    page_location: url,
    page_title: title || document.title,
  });

  if (typeof window.gtag === 'function' && GA4_MEASUREMENT_ID) {
    window.gtag('config', GA4_MEASUREMENT_ID, {
      page_path: pathname,
      page_location: url,
      page_title: title || document.title,
    });
  }
}
